import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useFluentContext } from '@fluentui/react-northstar';
import { pillsThatFit } from './tagPillsFit';
import { EMPTY_CELL } from './emptyCell';
import TooltipStyles from '../TicketDetailInfo/UsersTooltip.module.css';
import { ThemeColorScheme } from '../../common/TeamsTheme';

/** A pill, coloured from the theme variables the list sets (ThemeColorScheme) like the chips in the Tags panel. */
const PILL_STYLE: React.CSSProperties = {
  // 0 top, 2px bottom: the text 1px higher than an even 1px / 1px, so it sits at the pill's middle as on the board.
  flexShrink: 0, padding: '0 8px 2px', fontSize: 12, lineHeight: '18px', whiteSpace: 'nowrap',
  // The whole pill 1px down: centred in its pill, the text sat 0.9px above the row's plain text and the status and
  // SLA pills beside it (Shaun, 2026-10-19).
  position: 'relative', top: 1,
  // --tikit-pill-bg: a lighter grey in dark mode (main.css), where the theme's background barely stood off the row.
  backgroundColor: 'var(--tikit-pill-bg, var(--mgt-theme-background))', color: 'var(--mgt-theme-foreground)'
};
export const PILL_GAP = 4;
// The "+N" for pills that do not fit: plain text in the row's font, not a pill (Shaun) - shared with the SLA cell, so the
// two read the same and sit the same distance from their pills. Centred on the row, then lifted 1.5px: the eye reads a
// count against the pill beside it, and centred it sat 0.4px below the pill's middle where the SLA count had sat 1.2px
// above it.
export const COUNT_STYLE: React.CSSProperties = {
  flexShrink: 0, alignSelf: 'center', position: 'relative', top: -1.5, padding: '0 4px', whiteSpace: 'nowrap',
  cursor: 'default', fontSize: 12, fontWeight: 600, color: 'var(--mgt-theme-default-foreground1, currentColor)'
};

const COUNT_LIFT_BESIDE_PILLS = -0.5;

const CARD_WIDTH = 240;
const CARD_GAP = 8;

/**
 * The "+N" pill, and on hover a card naming the values it stands for, as pills - the Requester and Assignee cells' "+N"
 * card (UsersTooltip), titled with the column's name. Portalled and fixed, so the grid cannot clip it; below the pill,
 * or above it near the bottom of the window.
 */
const MorePills = ({ names, title }: { names: string[]; title: string }) => {
  const { siteVariables } = useFluentContext().theme;
  const pillRef = useRef<HTMLSpanElement>(null);
  const hideRef = useRef<ReturnType<typeof setTimeout>>();
  const [place, setPlace] = useState<{ left: number; top?: number; bottom?: number } | null>(null);

  const show = () => {
    clearTimeout(hideRef.current);
    const rect = pillRef.current?.getBoundingClientRect();
    if (!rect) return;
    const left = Math.max(CARD_GAP, Math.min(rect.left, window.innerWidth - CARD_WIDTH - CARD_GAP));
    setPlace(rect.bottom + 220 > window.innerHeight
      ? { left, bottom: window.innerHeight - rect.top + CARD_GAP }
      : { left, top: rect.bottom + CARD_GAP });
  };
  const hide = () => { hideRef.current = setTimeout(() => setPlace(null), 100); };
  useEffect(() => () => clearTimeout(hideRef.current), []);

  return (
    <>
      {/* 1px lower than the SLA cell's count: these pills sit 1px lower (PILL_STYLE), and the count keeps its place beside them. */}
      <span ref={pillRef} style={{ ...COUNT_STYLE, top: COUNT_LIFT_BESIDE_PILLS }} onMouseEnter={show} onMouseLeave={hide}>+{names.length}</span>
      {place && ReactDOM.createPortal(
        <div
          className={TooltipStyles.tooltipcontent}
          // Portalled out of the list, so the theme's variables are set again here, as UsersTooltip does.
          style={{ position: 'fixed', ...place, width: CARD_WIDTH, maxHeight: 200, overflow: 'auto', zIndex: 9999999, ...ThemeColorScheme(siteVariables) }}
          onMouseEnter={show}
          onMouseLeave={hide}
        >
          <div className={`font-sans font-medium py-1.5 pl-2 ${TooltipStyles.borderBottom}`} style={{ color: 'var(--mgt-theme-foreground)' }}>
            {title}
          </div>
          <div className="flex flex-wrap p-2" style={{ gap: PILL_GAP }}>
            {names.map((name, index) => (
              // The card is the pills' own colour, so in it they take the grey the list's rows sit on.
              <span key={`${name}-${index}`} className="rounded-full" style={{ ...PILL_STYLE, flexShrink: 1, whiteSpace: 'normal', backgroundColor: 'var(--mgt-theme-background2)' }}>
                <bdi>{name}</bdi>
              </span>
            ))}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

/**
 * A cell's several values - tags, a multi-select custom property's picks, a multi-choice form answer - as pills, in
 * the order given, on one line. Those that do not fit fold into a "+N" pill whose hover card, titled `title`, names
 * them. The cell measures again whenever its width changes, so widening the column brings folded pills back. The
 * pills are measured once, laid out out of sight, rather than on every resize.
 */
export const PillList = ({ names, title }: { names: string[]; title: string }) => {
  // By content, not identity: callers build the list afresh on every render.
  const namesKey = JSON.stringify(names);
  const boxRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(names.length);

  useLayoutEffect(() => {
    const box = boxRef.current;
    const measure = measureRef.current;
    if (!box || !measure || names.length === 0) return undefined;
    const pills = Array.from(measure.children) as HTMLElement[];
    const widths = pills.slice(0, names.length).map(pill => pill.getBoundingClientRect().width);
    const plusWidth = pills[names.length].getBoundingClientRect().width;
    const fit = () => setShown(pillsThatFit(widths, box.clientWidth, PILL_GAP, plusWidth));
    fit();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(fit);
    observer.observe(box);
    return () => observer.disconnect();
  }, [namesKey]);

  // No values: the dash every list shows for an empty field. After the hooks, which must always run.
  if (names.length === 0) return <span>{EMPTY_CELL}</span>;
  // Too narrow for one whole pill: the first is still drawn, cut short with an ellipsis, so a cell never shows "+N" alone.
  const whole = Math.max(shown, 0);
  const squeezed = whole === 0 && names.length > 0;
  const drawn = squeezed ? 1 : whole;
  const hidden = names.length - drawn;
  return (
    <div
      ref={boxRef}
      className="w-full mr-4 flex items-center overflow-hidden relative"
      style={{ gap: PILL_GAP, minWidth: 0 }}
    >
      {names.slice(0, drawn).map((name, index) => (
        <span
          key={`${name}-${index}`}
          className="rounded-full"
          style={squeezed ? { ...PILL_STYLE, flexShrink: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' } : PILL_STYLE}
          title={squeezed ? name : undefined}
        >
          {name}
        </span>
      ))}
      {hidden > 0 && <MorePills names={names.slice(drawn)} title={title} />}
      <div ref={measureRef} aria-hidden className="flex absolute" style={{ visibility: 'hidden', left: 0, top: 0, pointerEvents: 'none' }}>
        {names.map((name, index) => (
          <span key={`${name}-${index}`} className="rounded-full" style={PILL_STYLE}><bdi>{name}</bdi></span>
        ))}
        <span style={COUNT_STYLE}>+{names.length}</span>
      </div>
    </div>
  );
};
